/*
 * Order Details controller — renders /apps/frido/order?orderId=<id> as the
 * redesigned per-delivery view: a card per delivery group (products grouped
 * together, each Free Gift Coupon split into its own card), followed by the
 * Order Details / Price Breakdown / Eligibility accordions.
 *
 * Grouping: the proxy Order shape exposes flat lineItems + fulfillments but no
 * per-fulfillment line-item membership, so real products render as ONE delivery
 * group whose status comes from the most recent fulfillment. Coupons are split
 * out purely by title keyword. When a second delivery group exists (a coupon,
 * or a future backend that groups fulfillments) each card gets a "Delivery #N"
 * header + item count; a lone group renders headerless.
 *
 * Status is order-level per group. Field access is shape-tolerant
 * (image.url|imageUrl, discountedTotalSet|lineTotal, subtotalPrice|
 * subtotalPriceSet, totalPrice|totalPriceSet) because the deployed order shape
 * and the curated mapping differ on a few names.
 *
 * The coupon info drawer mirrors the order-list drawer exactly (same 4-step
 * tracker + unlocked "code shared" state); both must stay visually in sync.
 * Cancel + Track fire their own /apps/frido calls on demand.
 */
(function () {
  "use strict";

  var S = "stroke='currentColor' stroke-linecap='round' stroke-linejoin='round'";
  var ICON = {
    back: "<svg viewBox='0 0 24 24' width='22' height='22' fill='none' stroke-width='2' " + S + "><line x1='19' y1='12' x2='5' y2='12'></line><polyline points='12 19 5 12 12 5'></polyline></svg>",
    copy: "<svg viewBox='0 0 18 18' width='18' height='18' fill='none' stroke-width='1.4' " + S + "><rect x='5.25' y='5.25' width='10.5' height='10.5' rx='2'></rect><path d='M3 12.56A2 2 0 0 1 2.25 11.25V3.75A1.5 1.5 0 0 1 3.75 2.25h7.5c.56 0 .87.29 1.12.75'></path></svg>",
    help: "<svg viewBox='0 0 16 16' width='14' height='14' fill='none' stroke-width='1.33' " + S + "><path d='M2.67 10V8a5.33 5.33 0 0 1 10.66 0v2'></path><path d='M13.33 10.33A1.33 1.33 0 0 1 12 11.67h-.67V8.33H12a1.33 1.33 0 0 1 1.33 1.34ZM2.67 10.33A1.33 1.33 0 0 0 4 11.67h.67V8.33H4a1.33 1.33 0 0 0-1.33 1.34Z'></path></svg>",
    truck: "<svg viewBox='0 0 14 14' width='14' height='14' fill='currentColor'><path d='M7.58 2.33c.15 0 .3.06.41.17.11.11.17.26.17.41H10.5c.09 0 .17.02.25.06.08.04.15.09.2.16l.05.06 1.75 2.92.03.06.02.06.02.06.01.06v3.55c0 .15-.06.3-.17.41a.58.58 0 0 1-.41.17h-.68a1.75 1.75 0 0 1-3.3 0H5.73a1.75 1.75 0 0 1-3.3 0H1.75a.58.58 0 0 1-.58-.58V3.5c0-.31.12-.61.34-.83.22-.22.51-.34.82-.34h5.25ZM4.08 9.33a.58.58 0 1 0 0 1.17.58.58 0 0 0 0-1.17Zm5.84 0a.58.58 0 1 0 0 1.17.58.58 0 0 0 0-1.17ZM10.17 4.08H8.17v1.75h3.05l-1.05-1.75Z'></path></svg>",
    truck18: "<svg viewBox='0 0 18 18' width='18' height='18' fill='none' stroke-width='1.1' " + S + "><circle cx='5.25' cy='12.75' r='1.5'></circle><circle cx='12.75' cy='12.75' r='1.5'></circle><path d='M3.75 12.75H2.25V9.75M1.5 3.75h8.25v9M6.75 12.75H11.25M9.75 4.5h3.75l2.25 3.75v4.5h-1.5M15.75 8.25H9.75'></path></svg>",
    check: "<svg viewBox='0 0 14 14' width='14' height='14' fill='none' stroke-width='1.2' " + S + "><circle cx='7' cy='7' r='5.25'></circle><path d='M4.75 7l1.6 1.6L9.4 5.4'></path></svg>",
    x: "<svg viewBox='0 0 14 14' width='14' height='14' fill='none' stroke-width='1.2' " + S + "><circle cx='7' cy='7' r='5.25'></circle><path d='M8.75 5.25l-3.5 3.5M5.25 5.25l3.5 3.5'></path></svg>",
    lock: "<svg viewBox='0 0 14 14' width='14' height='14' fill='currentColor'><path d='M7 1.17a2.92 2.92 0 0 1 2.92 2.92v1.75a1.75 1.75 0 0 1 1.75 1.75v3.5a1.75 1.75 0 0 1-1.75 1.75H4.08a1.75 1.75 0 0 1-1.75-1.75v-3.5a1.75 1.75 0 0 1 1.75-1.75V4.08A2.92 2.92 0 0 1 7 1.17ZM7 8.17a1.17 1.17 0 1 0 0 2.33 1.17 1.17 0 0 0 0-2.33ZM7 2.33a1.75 1.75 0 0 0-1.75 1.75v1.75h3.5V4.08A1.75 1.75 0 0 0 7 2.33Z'></path></svg>",
    info: "<svg viewBox='0 0 18 18' width='18' height='18' fill='none' stroke-width='1.3' " + S + "><circle cx='9' cy='9' r='6.75'></circle><path d='M9 12V8.6'></path><path d='M9 6.2h.01'></path></svg>",
    close: "<svg viewBox='0 0 24 24' width='24' height='24' fill='none' stroke-width='1.5' " + S + "><path d='M18 6 6 18'></path><path d='M6 6l12 12'></path></svg>",
    // Muted info mark for the closed return window. Stroke rides currentColor so it
    // always matches the surrounding label rather than pinning the design's grey.
    infoClosed: "<svg viewBox='0 0 14 14' width='14' height='14' fill='none' stroke-width='1' " + S + "><path d='M1.75 7C1.75 7.68944 1.8858 8.37213 2.14963 9.00909C2.41347 9.64605 2.80018 10.2248 3.28769 10.7123C3.7752 11.1998 4.35395 11.5865 4.99091 11.8504C5.62787 12.1142 6.31056 12.25 7 12.25C7.68944 12.25 8.37213 12.1142 9.00909 11.8504C9.64605 11.5865 10.2248 11.1998 10.7123 10.7123C11.1998 10.2248 11.5865 9.64605 11.8504 9.00909C12.1142 8.37213 12.25 7.68944 12.25 7C12.25 5.60761 11.6969 4.27226 10.7123 3.28769C9.72774 2.30312 8.39239 1.75 7 1.75C5.60761 1.75 4.27226 2.30312 3.28769 3.28769C2.30312 4.27226 1.75 5.60761 1.75 7Z'></path><path d='M7 5H7.00583'></path><path d='M6.41699 6.75H7.00033V9.08333H7.58366'></path></svg>",
    howto: "<svg viewBox='0 0 18 18' width='18' height='18' fill='none' stroke-width='1.25' " + S + "><path d='M6.75 3.75H5.25A1.5 1.5 0 0 0 3.75 5.25v9A1.5 1.5 0 0 0 5.25 15.75h7.5A1.5 1.5 0 0 0 14.25 14.25v-9A1.5 1.5 0 0 0 12.75 3.75H11.25'></path><path d='M6.75 3.75A1.5 1.5 0 0 1 8.25 2.25h1.5a1.5 1.5 0 0 1 0 3h-1.5a1.5 1.5 0 0 1-1.5-1.5Z'></path><path d='M6.75 9h4.5'></path><path d='M6.75 12h4.5'></path></svg>",
    receipt: "<svg viewBox='0 0 18 18' width='18' height='18' fill='none' stroke-width='1.25' " + S + "><path d='M4.5 2.25h9a.75.75 0 0 1 .75.75v12.75l-2.06-1.37-1.44 1.37-1.5-1.37-1.5 1.37-1.44-1.37-2.06 1.37V3a.75.75 0 0 1 .75-.75Z'></path><path d='M6.75 6.75h4.5M6.75 9.75h4.5'></path></svg>",
    chev: "<svg class='fr-od__acc-chev' viewBox='0 0 20 20' width='20' height='20' fill='none' stroke-width='1.5' " + S + "><path d='M5 7.5l5 5 5-5'></path></svg>",
    stepCheck: "<svg viewBox='0 0 28 28' width='16' height='16' fill='none' stroke='#fff' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'><path d='M9 14l3 3 6-6'></path></svg>",
    box: "<svg viewBox='0 0 24 24' width='14' height='14' fill='none' stroke='#fff' stroke-width='1.7' stroke-linecap='round' stroke-linejoin='round'><path d='M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z'></path><path d='M3.27 6.96 12 12.01l8.73-5.05'></path><path d='M12 22.08V12'></path></svg>",
    tlChev: "<svg viewBox='0 0 20 20' width='18' height='18' fill='none' stroke='#101820' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M5 7.5l5 5 5-5'></path></svg>",
    xmark: "<svg viewBox='0 0 24 24' width='13' height='13' fill='none' stroke='#fff' stroke-width='2.4' stroke-linecap='round'><path d='M6 6l12 12M18 6L6 18'></path></svg>",
    undo: "<svg viewBox='0 0 24 24' width='14' height='14' fill='none' stroke='#fff' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'><path d='M9 14 4 9l5-5'></path><path d='M4 9h11a5 5 0 0 1 0 10h-2'></path></svg>",
    rupee: "<svg viewBox='0 0 24 24' width='13' height='13' fill='none' stroke='#fff' stroke-width='1.9' stroke-linecap='round' stroke-linejoin='round'><path d='M7 5h10M7 9h10M14 5c0 5-3.5 5.5-6.5 5.5L15 19'></path></svg>",
    truck14: "<svg viewBox='0 0 14.4 14.4' width='14.4' height='14.4' fill='none' stroke-width='0.8' " + S + "><circle cx='4.2' cy='10.2' r='1.2'></circle><circle cx='10.2' cy='10.2' r='1.2'></circle><path d='M3 10.2H1.8V7.8M1.2 3h6.6v7.2M5.4 10.2H9M7.8 3.6h3l1.8 3v3.6h-1.2M12.6 6.6H7.8'></path><path d='M1.8 5.4h2.4'></path></svg>",
    home14: "<svg viewBox='0 0 14.4 14.4' width='14.4' height='14.4' fill='none' stroke-width='0.8' " + S + "><path d='M5.4 12.6V9a1.2 1.2 0 0 1 1.2-1.2h1.2A1.2 1.2 0 0 1 9 9'></path><path d='M11.4 8.09V7.2h1.2L7.2 1.8 1.8 7.2H3v4.2a1.2 1.2 0 0 0 1.2 1.2h2.72'></path><path d='M9 11.4l1.2 1.2 2.4-2.4'></path></svg>",
    mail14: "<svg viewBox='0 0 14.4 14.4' width='14.4' height='14.4' fill='none' stroke-width='0.8' " + S + "><path d='M11.4 13.5a3 3 0 0 1 2.1-2.1 3 3 0 0 1-2.1-2.1 3 3 0 0 1-2.1 2.1 3 3 0 0 1 2.1 2.1Z'></path><path d='M6.9 11.4H3a1.2 1.2 0 0 1-1.2-1.2V4.2A1.2 1.2 0 0 1 3 3h8.4a1.2 1.2 0 0 1 1.2 1.2v3'></path><path d='M1.8 4.2l5.4 3.6 5.4-3.6'></path></svg>",
    mail20: "<svg viewBox='0 0 20 20' width='20' height='20' fill='none' stroke-width='1.11' " + S + "><path d='M15.83 18.75a3 3 0 0 1 2.92-2.92 3 3 0 0 1-2.92-2.92 3 3 0 0 1-2.92 2.92 3 3 0 0 1 2.92 2.92Z'></path><path d='M9.58 15.83H4.17a1.67 1.67 0 0 1-1.67-1.67V5.83A1.67 1.67 0 0 1 4.17 4.17h11.66a1.67 1.67 0 0 1 1.67 1.66V10'></path><path d='M2.5 5.83 10 10.83l7.5-5'></path></svg>",
    checkGreen: "<svg viewBox='0 0 24 24' width='22' height='22' fill='#1f9d4d'><path d='M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-1.2 14.3-3.5-3.5 1.4-1.4 2.1 2.1 4.3-4.3 1.4 1.4-5.7 5.7Z'></path></svg>",
  };

  var TERMS =
    "<p>Orders above ₹1,499 qualify for a Free Gift Coupon, which will be automatically added to your cart.</p>" +
    "<p>• The coupon will be sent to you within 7 days after your order is delivered.</p>" +
    "<p>• Enjoy the coupon amount OFF on your next qualifying purchase.</p>" +
    "<p>• Coupons are valid only through the shared offer link and cannot be combined with other discounts or Gift Vouchers.</p>" +
    "<p>• Each coupon can be used once during the offer period.</p>" +
    "<p>• Frido reserves the right to change or withdraw the offer at any time without prior notice.</p>";

  // Scroll-lock. body{overflow:hidden} alone does NOT hold the background — on
  // desktop the scroll container is <html>, not <body> — which is why the order
  // page scrolled behind the coupon popup. Fixing the body (preserving the
  // offset in `top`) locks every device. Depth-counted because the coupon
  // drawer, cancel modal and tracking drawer can overlap; the last one out
  // restores. Same approach as sai-snippet-fsgfrd01.js.
  var _lock = { depth: 0, y: 0, prev: null };
  function lockScroll() {
    if (_lock.depth++ > 0) return;
    _lock.y = window.scrollY || window.pageYOffset || 0;
    var b = document.body.style;
    _lock.prev = { overflow: b.overflow, position: b.position, top: b.top, width: b.width };
    b.overflow = "hidden";
    b.position = "fixed";
    b.top = "-" + _lock.y + "px";
    b.width = "100%";
  }
  function unlockScroll() {
    if (_lock.depth === 0) return;
    if (--_lock.depth > 0) return;
    var b = document.body.style, p = _lock.prev || {};
    b.overflow = p.overflow || "";
    b.position = p.position || "";
    b.top = p.top || "";
    b.width = p.width || "";
    window.scrollTo(0, _lock.y);
  }

  /* Listeners on `document` must NOT be registered from inside init(): the account drawer
     calls FridoOrderDetails.init() on EVERY open (account-desktop.js openDrawer clones the
     template fresh each time), so anything bound to document accumulates one handler per
     open and is never removed — the handlers are anonymous, so they cannot be.

     For the accordion that was the visible bug: the handler TOGGLES, so N listeners toggle
     N times. Open the drawer once and a click works; twice and the click is a no-op; three
     times and it works again. "Sometimes it closes, sometimes it doesn't" is that parity.

     The accordion handler reads everything it needs off the event, so it binds once for the
     page. The Escape handlers close over per-init state, so they are re-bound per init but
     the previous one is removed first — idempotent close() calls hid this, but it still
     leaked a handler per open and pinned detached DOM.
  */
  var accDelegated = false;
  var escDrawerFn = null, escTrackFn = null, escModalFn = null, escCancelFn = null;

  function init(root) {
    var F = window.FridoOrders;
    var detailEl = root.querySelector("[data-fr-detail]");
    var orderNoWrap = root.querySelector("[data-fr-orderno-wrap]");
    var orderNoEl = root.querySelector("[data-fr-orderno]");
    var copyBtn = root.querySelector("[data-fr-copy]");
    var drawer = root.querySelector("[data-fr-coupon-drawer]");
    var drawerBody = root.querySelector("[data-fr-coupon-body]");
    var modal = root.querySelector("[data-fr-modal]");
    var modalBody = root.querySelector("[data-fr-modal-body]");
    var trackDrawer = root.querySelector("[data-fr-track-drawer]");
    var trackDrawerBody = root.querySelector("[data-fr-track-body]");

    var listPath = root.getAttribute("data-list-path") || "/pages/orders";
    var loginPath = root.getAttribute("data-login-path") || "/customer_authentication/login";
    var walletPath = root.getAttribute("data-wallet-path") || "/pages/wallet";
    var returnsUrl = root.getAttribute("data-returns-url") || "";
    var returnWindowDays = parseInt(root.getAttribute("data-return-window-days"), 10);
    if (isNaN(returnWindowDays)) returnWindowDays = 7;
    var couponKeyword = (root.getAttribute("data-coupon-keyword") || "gift coupon").toLowerCase();
    var whatsappPhone = root.getAttribute("data-whatsapp-phone") || "";
    var shippingMethod = root.getAttribute("data-shipping-method") || "Standard Delivery";
    var policyTitle = root.getAttribute("data-policy-title") || "Eligibility on Replacement";
    var policyBody = root.getAttribute("data-policy-body") || "";

    // Drawer reuse (desktop account shell): the order id comes from the
    // container's data-order-id; the standalone page reads it from the URL.
    var orderId = root.getAttribute("data-order-id") || new URLSearchParams(window.location.search).get("orderId");
    var current = null;

    // ── Help (WhatsApp) ─────────────────────────────────────────────────
    function buildHelpUrl(orderName) {
      if (!whatsappPhone) return "#";
      var msg = orderName
        ? "Hi, my order number is: " + orderName + ". I need support with my order."
        : "Hi, I need support with my order.";
      return "https://api.whatsapp.com/send?phone=" + encodeURIComponent(whatsappPhone) + "&text=" + encodeURIComponent(msg);
    }
    function setHelpLinks(orderName) {
      var url = buildHelpUrl(orderName);
      root.querySelectorAll("[data-fr-help]").forEach(function (el) {
        el.setAttribute("href", url);
        if (url !== "#") { el.setAttribute("target", "_blank"); el.setAttribute("rel", "noopener"); }
      });
    }
    setHelpLinks(null);

    var backBtn = root.querySelector("[data-fr-back]");
    if (backBtn) backBtn.addEventListener("click", function () { if (history.length > 1) history.back(); else window.location.href = listPath; });

    if (copyBtn) {
      copyBtn.addEventListener("click", function () {
        var text = (current && current.name) || "";
        if (navigator.clipboard && text) navigator.clipboard.writeText(text).catch(function () {});
        copyBtn.classList.add("is-copied");
        if (text && F && F.toast) F.toast("Order ID Copied to Clipboard");
        setTimeout(function () { copyBtn.classList.remove("is-copied"); }, 900);
      });
    }

    // ── Field accessors (shape-tolerant) ────────────────────────────────
    function liImg(li) { return (li.image && li.image.url) || li.imageUrl || null; }
    function liAlt(li) { return (li.image && li.image.altText) || li.imageAltText || li.title || ""; }
    function liPaid(li) { return li.discountedTotalSet || li.lineTotal || li.originalUnitPriceSet || li.unitPrice || null; }
    function isCoupon(li) { return (li.title || "").toLowerCase().indexOf(couponKeyword) !== -1; }
    function couponAmountStr(li) {
      var m = (li.title || "").match(/₹\s?([\d,]+)/);
      if (m) return "₹" + m[1];
      return F.money(liPaid(li)) || "";
    }
    function asMoney(x) { if (!x) return null; if (x.amount != null) return x; if (x.shopMoney) return x.shopMoney; return null; }
    function amtOf(m) { m = asMoney(m); return m && m.amount != null ? Number(m.amount) : 0; }
    function curOf(m) { m = asMoney(m); return (m && m.currencyCode) || "INR"; }
    function moneyOfAmount(n, currency) { return F.money({ amount: String(n), currencyCode: currency || "INR" }); }

    // ── Status (order-level, per delivery group) ────────────────────────
    function isToday(iso) {
      if (!iso) return false;
      var d = new Date(iso); if (isNaN(d.getTime())) return false;
      var n = new Date();
      return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth() && d.getDate() === n.getDate();
    }
    function orderStatus(order) {
      if (F.isCancelled(order)) return { label: "Cancelled", tone: "red", icon: ICON.x, kind: "cancelled" };
      var f = (order.fulfillments || [])[0] || null;
      var ds = f && f.displayStatus, eta = f && f.estimatedDeliveryAt;
      switch (ds) {
        case "DELIVERED": return { label: eta ? "Delivered on " + F.formatDate(eta) : "Delivered", tone: "green", icon: ICON.check, kind: "delivered" };
        case "OUT_FOR_DELIVERY": return { label: isToday(eta) ? "Out for delivery, Today" : "Out for delivery", tone: "blue", icon: ICON.truck, kind: "pending" };
        case "IN_TRANSIT": return { label: eta ? "Expected by " + F.formatDate(eta) : "In transit", tone: "gray", icon: ICON.truck, kind: "pending" };
        case "ATTEMPTED_DELIVERY": return { label: "Delivery attempted", tone: "gray", icon: ICON.truck, kind: "pending" };
        case "ON_HOLD": return { label: "On hold", tone: "gray", icon: ICON.truck, kind: "pending" };
      }
      if (eta) return { label: "Expected by " + F.formatDate(eta), tone: "gray", icon: ICON.truck, kind: "pending" };
      if (order.displayFulfillmentStatus === "FULFILLED") return { label: "Shipped", tone: "blue", icon: ICON.truck, kind: "pending" };
      return { label: "Order Placed", tone: "gray", icon: ICON.truck, kind: "pending" };
    }

    // ── Coupon status (unlock = delivered/est date + 7 days) ────────────
    function orderDelivered(order) { return (order.fulfillments || []).some(function (f) { return f.displayStatus === "DELIVERED"; }); }
    function orderShipped(order) { return (order.fulfillments || []).length > 0 || order.displayFulfillmentStatus === "FULFILLED" || order.displayFulfillmentStatus === "PARTIALLY_FULFILLED"; }
    function deliveredBasisIso(order) {
      var fs = order.fulfillments || [];
      for (var i = 0; i < fs.length; i++) if (fs[i].displayStatus === "DELIVERED" && fs[i].estimatedDeliveryAt) return fs[i].estimatedDeliveryAt;
      for (var j = 0; j < fs.length; j++) if (fs[j].estimatedDeliveryAt) return fs[j].estimatedDeliveryAt;
      return null;
    }
    function couponInfo(order) {
      var basis = deliveredBasisIso(order), unlockIso = null;
      if (basis) { var d = new Date(basis); if (!isNaN(d.getTime())) { d.setDate(d.getDate() + 7); unlockIso = d.toISOString(); } }
      var deliveredNow = orderDelivered(order);
      var unlocked = !!(unlockIso && deliveredNow && Date.now() >= new Date(unlockIso).getTime());
      // A cancelled order cancels its coupon too. Without this the coupon card
      // kept its delivery-clock state on a fully cancelled order and rendered
      // "Delivery pending" next to three Cancelled products.
      return {
        unlockIso: unlockIso,
        deliveredNow: deliveredNow,
        unlocked: unlocked,
        cancelled: F.isCancelled(order),
      };
    }
    function maskPhone(p) {
      if (!p) return "";
      var digits = String(p).replace(/\s+/g, "");
      if (digits.length < 4) return digits;
      return digits.slice(0, digits.length - 4) + "****";
    }

    // ── Return/Exchange window (delivered basis + N days) ───────────────
    function returnWindowOpen(order) {
      // Per-fulfillment estimate first, then the order-level one. Fulfillments
      // routinely carry a null estimate even when their status is DELIVERED, and
      // with no basis at all the window can never close — the order-level date is
      // the only delivery date those orders expose.
      var basis = deliveredBasisIso(order) || order.estimatedDeliveryDate;
      if (!basis) return true; // no delivery date anywhere → keep the window open
      var d = new Date(basis); if (isNaN(d.getTime())) return true;
      d.setDate(d.getDate() + returnWindowDays);
      return Date.now() <= d.getTime();
    }

    // ── Render primitives ───────────────────────────────────────────────
    function statusPill(st) {
      return '<span class="fr-od__pill fr-od__pill--' + st.tone + '"><span class="fr-od__pill-icon">' + st.icon + '</span><span class="fr-od__pill-text">' + F.escapeHtml(st.label) + "</span></span>";
    }
    function couponChip(amt) {
      return '<div class="fr-od__coupon-chip"><span class="fr-od__coupon-chip-free">Free Coupon</span><span class="fr-od__coupon-chip-amt">' + F.escapeHtml(amt) + "</span></div>";
    }
    function couponPill(ci, cls) {
      cls = cls || "fr-od__pill";
      // Cancellation wins over every delivery-clock state — Shopify's cancelledAt
      // is the authority, same as the product pill. Must be checked FIRST: an
      // unlocked coupon on a cancelled order would otherwise claim "Delivered on".
      if (ci.cancelled) return '<span class="' + cls + " " + cls + '--red"><span class="' + cls + '-icon">' + ICON.x + '</span><span class="' + cls + '-text">Cancelled</span></span>';
      if (ci.unlocked) return '<span class="' + cls + " " + cls + '--green"><span class="' + cls + '-icon">' + ICON.check + '</span><span class="' + cls + '-text">Delivered on ' + F.escapeHtml(F.formatDate(ci.unlockIso)) + "</span></span>";
      var label = ci.unlockIso ? "Delivery by, " + F.formatDate(ci.unlockIso) : "Delivery pending";
      return '<span class="' + cls + " " + cls + '--gray"><span class="' + cls + '-icon">' + ICON.lock + '</span><span class="' + cls + '-text">' + F.escapeHtml(label) + "</span></span>";
    }
    function productRow(li, st) {
      var url = liImg(li);
      var img = url ? '<img src="' + F.escapeHtml(url) + '" alt="' + F.escapeHtml(liAlt(li)) + '" loading="lazy">' : "";
      var qty = li.quantity || 1;
      var paid = F.money(liPaid(li));
      // Selected variant options (e.g. "Color: Green · Size: L") — shown before Qty,
      // matching the tracking drawer's trackItemMeta and the Figma. Use the structured
      // selectedOptions so each option renders with its own name; fall back to the raw
      // variantTitle. Skip the single-variant placeholder so unvaried products stay "Qty · Paid".
      var opts = (Array.isArray(li.selectedOptions) ? li.selectedOptions : []).filter(function (o) {
        return o && o.name && o.value && String(o.value).toLowerCase() !== "default title";
      });
      var meta = "";
      if (opts.length) {
        meta += opts
          .map(function (o) { return "<span>" + F.escapeHtml(o.name) + ": " + F.escapeHtml(o.value) + "</span>"; })
          .join('<span class="fr-od__dot"></span>');
        meta += '<span class="fr-od__dot"></span>';
      } else {
        var variant = li.variantTitle && li.variantTitle.toLowerCase() !== "default title" ? li.variantTitle : null;
        if (variant) meta += "<span>" + F.escapeHtml(variant) + '</span><span class="fr-od__dot"></span>';
      }
      meta += "<span>Qty: " + qty + "</span>";
      if (paid) meta += '<span class="fr-od__dot"></span><span>Paid: ' + F.escapeHtml(paid) + "</span>";
      return (
        '<div class="fr-od__item"><div class="fr-od__thumb">' + img + "</div>" +
        '<div class="fr-od__item-main">' + statusPill(st) +
        '<span class="fr-od__item-title">' + F.escapeHtml(li.title || "") + "</span>" +
        '<div class="fr-od__item-meta">' + meta + "</div></div></div>"
      );
    }
    function couponRow(li, order) {
      var ci = couponInfo(order);
      var amt = couponAmountStr(li);
      var title = li.title || ("Free Gift Coupon - " + amt);
      return (
        '<div class="fr-od__item fr-od__item--coupon">' + couponChip(amt) +
        '<div class="fr-od__item-main">' + couponPill(ci) + '<span class="fr-od__coupon-title">' + F.escapeHtml(title) + "</span></div>" +
        // No info popup for a cancelled coupon — there is nothing to unlock or
        // explain, so the (i) trigger is dropped entirely.
        (ci.cancelled ? "" : '<button type="button" class="fr-od__coupon-info" data-fr-coupon aria-label="Coupon details">' + ICON.info + "</button>") + "</div>"
      );
    }
    function divider() { return '<div class="fr-od__idiv"></div>'; }

    function actionsHtml(order, st) {
      if (st.kind === "cancelled") return "";
      if (st.kind === "delivered") {
        if (returnWindowOpen(order)) {
          return '<button type="button" class="fr-od__btn fr-od__btn--ghost fr-od__btn--full" data-fr-return>Return/Exchange</button>';
        }
        return '<div class="fr-od__closed">' + ICON.infoClosed + "<span>Return/Exchange window is closed for this order</span></div>";
      }
      return (
        '<div class="fr-od__btns">' +
        '<button type="button" class="fr-od__btn fr-od__btn--ghost" data-fr-cancel>Cancel</button>' +
        '<button type="button" class="fr-od__btn fr-od__btn--solid" data-fr-track>Track Order</button>' +
        "</div>"
      );
    }

    function deliveryHeader(index, count, unit) {
      return (
        '<div class="fr-od__dhead"><div class="fr-od__dhead-left">' +
        '<span class="fr-od__dhead-ic">' + ICON.truck18 + "</span>" +
        '<span class="fr-od__dhead-title">Delivery #' + index + "</span></div>" +
        '<span class="fr-od__dhead-count">' + count + " " + unit + (count === 1 ? "" : "s") + "</span></div>"
      );
    }

    function productCard(items, order, st, index, multi) {
      var rows = items.map(function (li, i) { return (i > 0 ? divider() : "") + productRow(li, st); }).join("");
      var acts = actionsHtml(order, st);
      if (acts) rows += divider() + acts;
      return (
        '<div class="fr-od__card">' +
        (multi ? deliveryHeader(index, items.length, "item") : "") +
        '<div class="fr-od__dbody">' + rows + "</div></div>"
      );
    }
    function couponCard(li, order, index, multi) {
      return (
        '<div class="fr-od__card">' +
        (multi ? deliveryHeader(index, 1, "item") : "") +
        '<div class="fr-od__dbody">' + couponRow(li, order) + "</div></div>"
      );
    }

    // ── Accordions ──────────────────────────────────────────────────────
    // Div-based accordion (not native <details>) so the body height can animate —
    // native <details> hides its body outright and can't transition. The clip
    // uses the grid-template-rows 1fr↔0fr technique (same as the cancel drawer's
    // .fr-cc__acc) driven by the .is-open class; the toggle is wired below.
    function accordion(title, iconSvg, bodyHtml, open) {
      return (
        '<div class="fr-od__acc' + (open ? " is-open" : "") + '">' +
        '<button type="button" class="fr-od__acc-sum" data-fr-acc aria-expanded="' + (open ? "true" : "false") + '">' +
        '<span class="fr-od__acc-left"><span class="fr-od__acc-ic">' + iconSvg + "</span>" +
        '<span class="fr-od__acc-title">' + F.escapeHtml(title) + "</span></span>" + ICON.chev + "</button>" +
        '<div class="fr-od__acc-clip"><div class="fr-od__acc-clipinner"><div class="fr-od__acc-body">' + bodyHtml + "</div></div></div></div>"
      );
    }
    function kvRow(k, vHtml, opts) {
      opts = opts || {};
      return (
        '<div class="fr-od__kv' + (opts.total ? " fr-od__kv--total" : "") + (opts.multi ? " fr-od__kv--multi" : "") + '">' +
        '<span class="fr-od__kv-k">' + F.escapeHtml(k) + "</span>" +
        '<span class="fr-od__kv-v">' + vHtml + "</span></div>"
      );
    }
    function kdiv() { return '<div class="fr-od__kdiv"></div>'; }

    function addressLines(a) {
      if (!a) return "";
      var cityLine = [a.city, a.province, a.zip].filter(Boolean).join(" ");
      var parts = [a.address1, a.address2, cityLine, a.country].filter(Boolean);
      return parts.map(function (p) { return F.escapeHtml(p); }).join("<br>");
    }
    function receiver(order) {
      var a = order.shippingAddress || {};
      var name = a.name || "";
      var phone = a.phone || order.phone || (order.customer && order.customer.phone) || "";
      return [name, phone].filter(Boolean).map(function (p) { return F.escapeHtml(p); }).join(", ");
    }
    function orderDetailsBody(order) {
      var addr = addressLines(order.shippingAddress) || "—";
      var billing = addressLines(order.billingAddress) || addr;
      var payment = order.displayFinancialStatus === "PAID" ? "Prepaid" : "COD";
      var placed = F.formatDate(order.createdAt);
      var t = order.createdAt ? new Date(order.createdAt) : null;
      var timeStr = "";
      if (t && !isNaN(t.getTime())) {
        var h = t.getHours(), ampm = h >= 12 ? "PM" : "AM", h12 = h % 12 || 12;
        var pad = function (n) { return n < 10 ? "0" + n : "" + n; };
        timeStr = pad(h12) + ":" + pad(t.getMinutes()) + " " + ampm;
      }
      var placedHtml = F.escapeHtml(placed) + (timeStr ? ' <span class="fr-od__dot fr-od__dot--dark"></span> ' + F.escapeHtml(timeStr) : "");
      var idHtml = F.escapeHtml(order.name || "") + '<button type="button" class="fr-od__kv-copy" data-fr-copy2 aria-label="Copy order number">' + ICON.copy + "</button>";
      return (
        kvRow("Order ID", idHtml) + kdiv() +
        kvRow("Reciever Details", receiver(order) || "—") + kdiv() +
        kvRow("Delivery Address", addr, { multi: true }) + kdiv() +
        kvRow("Billing Address", billing, { multi: true }) + kdiv() +
        kvRow("Order Placed at", placedHtml) + kdiv() +
        kvRow("Payment Mode", F.escapeHtml(payment)) + kdiv() +
        kvRow("Shipping Method", F.escapeHtml(shippingMethod))
      );
    }
    // Price Breakdown — sourced field-for-field from the order JSON per the design
    // dev comments:
    //   Selling Price               = subtotal_price
    //   Delivery Fee                = shipping_lines["Free Shipping"].price   (FREE when 0)
    //   COD Charges                 = shipping_lines["COD Charges"].price     (COD orders only)
    //   Discount Applied            = total_discounts                         (when > 0)
    //   Handling and Packaging Fees = shipping_lines["Convenience Fee"].price
    //   Total Price                 = total_price
    // Reads the REST shape (snake_case + shipping_lines[{title,price}]) and falls
    // back to the older GraphQL camelCase fields so it works whichever the proxy
    // sends. The per-line rows (COD / Handling) only render when their shipping_line
    // is present — prepaid orders omit COD, and older responses without
    // shipping_lines simply fall back to the aggregate delivery total.
    function priceBreakdownBody(order) {
      function num(v) { return v == null || v === "" ? null : Number(v); }
      var currency = order.currency || curOf(order.totalPrice || order.totalPriceSet) || "INR";

      var subtotal = num(order.subtotal_price);
      if (subtotal == null) subtotal = amtOf(order.subtotalPrice || order.subtotalPriceSet);
      var discount = num(order.total_discounts);
      if (discount == null) discount = amtOf(order.totalDiscounts || order.totalDiscountsSet);
      var total = num(order.total_price);
      if (total == null) total = amtOf(order.totalPrice || order.totalPriceSet);

      // shipping_lines: REST array of { title, price } OR a GraphQL connection.
      function shipLines() {
        var sl = order.shipping_lines || order.shippingLines;
        if (!sl) return null;
        if (Array.isArray(sl)) return sl;
        if (sl.edges) return sl.edges.map(function (e) { return e.node; });
        if (sl.nodes) return sl.nodes;
        return null;
      }
      var lines = shipLines();
      function lineAmount(title) {
        if (!lines) return null;
        for (var i = 0; i < lines.length; i++) {
          var l = lines[i];
          if (l && String(l.title).toLowerCase() === title.toLowerCase()) {
            if (l.price != null) return Number(l.price);
            return amtOf(l.discountedPriceSet || l.originalPriceSet || l.priceSet);
          }
        }
        return null;
      }
      function money(n) { return F.escapeHtml(moneyOfAmount(n, currency)); }
      function finalSpan(n) { return '<span class="fr-od__price-final">' + money(n) + "</span>"; }
      function greenSpan(html) { return '<span class="fr-od__price-green">' + html + "</span>"; }

      // 1. Selling Price
      var rows = kvRow("Selling Price", finalSpan(subtotal || 0));

      // 2. Delivery Fee — a FIXED ₹49 standard price shown struck through, then FREE.
      //    Delivery is ALWAYS free; the Free Shipping line is read only so that a
      //    real charge would appear if one is ever introduced. Do NOT fall back to
      //    the aggregate shipping total here — that total is COD Charges + Handling
      //    (e.g. ₹68 = ₹49 + ₹19), not delivery, and showing it as the Delivery Fee
      //    was the bug (an order read "Delivery Fee ₹49 → ₹68").
      var STD_DELIVERY_FEE = 49;
      var delivery = lineAmount("Free Shipping");
      var deliveryHtml = '<span class="fr-od__price-orig">' + money(STD_DELIVERY_FEE) + "</span>" +
        ((delivery && delivery > 0) ? finalSpan(delivery) : greenSpan("FREE"));
      rows += kdiv() + kvRow("Delivery Fee", deliveryHtml);

      // 3. COD Charges (COD orders only)
      var cod = lineAmount("COD Charges");
      if (cod != null) rows += kdiv() + kvRow("COD Charges", finalSpan(cod));

      // 4. Discount Applied
      if (discount && discount > 0) {
        rows += kdiv() + kvRow("Discount Applied", greenSpan(money(discount)));
      }

      // 5. Handling and Packaging Fees (Convenience Fee line)
      var handling = lineAmount("Convenience Fee");
      if (handling != null) rows += kdiv() + kvRow("Handling and Packaging Fees", finalSpan(handling));

      // 6. Total Price — the struck ORIGINAL is the full list price
      //    (Selling Price + fixed ₹49 delivery + COD Charges + Handling, per the dev
      //    comment); the unstruck FINAL is total_price, the actual amount paid. The
      //    gap is the free delivery and any discount. Struck only shows when it is
      //    higher than what was paid.
      var strikeTotal = (subtotal || 0) + STD_DELIVERY_FEE + (cod || 0) + (handling || 0);
      var totalHtml = strikeTotal > total
        ? '<span class="fr-od__price-orig">' + money(strikeTotal) + "</span>" + finalSpan(total)
        : finalSpan(total);
      rows += kdiv() + kvRow("Total Price", totalHtml, { total: true });
      return rows;
    }
    function policyBodyHtml() {
      if (!policyBody) return "";
      var lines = policyBody.split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
      return '<div class="fr-od__policy">' + lines.map(function (l) { return "<p>" + F.escapeHtml(l) + "</p>"; }).join("") + "</div>";
    }

    // ── Main render ─────────────────────────────────────────────────────
    function render(order) {
      current = order;
      var items = order.lineItems || [];
      var products = items.filter(function (li) { return !isCoupon(li); });
      var coupons = items.filter(isCoupon);
      var st = orderStatus(order);

      var groups = [];
      if (products.length) groups.push({ type: "products" });
      coupons.forEach(function (li) { groups.push({ type: "coupon", li: li }); });
      var multi = groups.length > 1;

      var idx = 0, cardsHtml = "";
      groups.forEach(function (g) {
        idx += 1;
        cardsHtml += g.type === "products"
          ? productCard(products, order, st, idx, multi)
          : couponCard(g.li, order, idx, multi);
      });

      detailEl.innerHTML =
        cardsHtml +
        accordion("Order Details", ICON.receipt, orderDetailsBody(order), true) +
        accordion("Price Breakdown", ICON.receipt, priceBreakdownBody(order), true) +
        accordion(policyTitle, ICON.howto, policyBodyHtml(), true);

      // Topbar order number.
      if (orderNoEl) orderNoEl.textContent = order.name || "";
      if (orderNoWrap) orderNoWrap.hidden = !order.name;

      // Wiring.
      var cancelBtn = detailEl.querySelector("[data-fr-cancel]");
      if (cancelBtn) cancelBtn.addEventListener("click", openCancel);
      var trackBtn = detailEl.querySelector("[data-fr-track]");
      if (trackBtn) trackBtn.addEventListener("click", openTrackDrawer);
      var returnBtn = detailEl.querySelector("[data-fr-return]");
      if (returnBtn) returnBtn.addEventListener("click", function () {
        var url = returnsUrl || buildHelpUrl(order.name);
        if (url && url !== "#") window.open(url, "_blank", "noopener");
      });
      detailEl.querySelectorAll("[data-fr-coupon]").forEach(function (btn) {
        btn.addEventListener("click", function () { openDrawer(order); });
      });
      var copy2 = detailEl.querySelector("[data-fr-copy2]");
      if (copy2) copy2.addEventListener("click", function () {
        if (navigator.clipboard && order.name) navigator.clipboard.writeText(order.name).catch(function () {});
        copy2.classList.add("is-copied");
        if (order.name && F && F.toast) F.toast("Order ID Copied to Clipboard");
        setTimeout(function () { copy2.classList.remove("is-copied"); }, 900);
      });

      setHelpLinks((order.name || "").replace(/^#/, ""));
    }

    // ── Coupon drawer (mirrors the order-list drawer) ───────────────────
    function step(done, iconSvg) {
      return done
        ? '<span class="fr-cp__step fr-cp__step--done">' + ICON.stepCheck + "</span>"
        : '<span class="fr-cp__step fr-cp__step--pending"><span class="fr-cp__step-icon">' + iconSvg + "</span></span>";
    }
    function trackerHtml(order, ci) {
      return '<div class="fr-cp__tracker">' +
        step(true, ICON.stepCheck) + '<span class="fr-cp__conn"></span>' +
        step(orderShipped(order), ICON.truck14) + '<span class="fr-cp__conn"></span>' +
        step(ci.deliveredNow, ICON.home14) + '<span class="fr-cp__conn"></span>' +
        step(ci.unlocked, ICON.mail14) + "</div>";
    }
    function drawerCouponCard(li, order, ci) {
      var amt = couponAmountStr(li);
      return '<div class="fr-cp__card"><div class="fr-cp__card-pad"><div class="fr-cp__crow">' + couponChip(amt) +
        '<div class="fr-cp__crow-main">' + couponPill(ci, "fr-cp__pill") + '<span class="fr-cp__ctitle">' + F.escapeHtml(li.title || ("Free Gift Coupon - " + amt)) + "</span></div></div></div></div>";
    }
    function drawerHtml(order, li) {
      var ci = couponInfo(order);
      var head = '<div class="fr-cp__head"><h2 class="fr-cp__head-title">Free Gift Coupons</h2><button type="button" class="fr-cp__close" data-fr-coupon-close aria-label="Close">' + ICON.close + "</button></div>";
      // Cancelled first: with no delivery coming, the unlock tracker and the
      // "Get it unlocked by" date are not just wrong but promise a date that can
      // never arrive. Show the cancellation instead of a countdown.
      if (ci.cancelled) {
        var cancelledNote = '<div class="fr-cp__card"><div class="fr-cp__shared">' +
          '<span class="fr-cp__shared-icon" style="color:#dc3634">' + ICON.x + "</span><div>" +
          '<div class="fr-cp__shared-title">This coupon was cancelled</div>' +
          '<div class="fr-cp__shared-sub">It was cancelled along with this order, so it won\'t be issued.</div>' +
          "</div></div></div>";
        return head + '<div class="fr-cp__col">' + drawerCouponCard(li, order, ci) + cancelledNote + "</div>" +
          '<button type="button" class="fr-cp__cta" data-fr-coupon-close>Continue</button>';
      }
      if (ci.unlocked) {
        var shared = '<div class="fr-cp__card"><div class="fr-cp__shared"><span class="fr-cp__shared-icon">' + ICON.checkGreen + "</span><div>" +
          '<div class="fr-cp__shared-title">Your Coupon Code is Shared to</div><div class="fr-cp__shared-sub">' + F.escapeHtml(maskPhone(order.phone || (order.customer && order.customer.phone))) + "</div></div></div></div>";
        var avail = '<div class="fr-cp__card"><div class="fr-cp__howhead"><div class="fr-cp__howhead-left"><span class="fr-cp__howicon">' + ICON.howto + '</span><span class="fr-cp__howtitle">How to Avail Gift coupon</span></div></div><div class="fr-cp__terms">' + TERMS + "</div></div>";
        // Wallet is de-listed from the store — the coupon is delivered to the
        // shopper's phone (shown above), so this just dismisses the drawer
        // rather than redirecting to the wallet page.
        return head + '<div class="fr-cp__col">' + drawerCouponCard(li, order, ci) + shared + avail + "</div>" +
          '<button type="button" class="fr-cp__cta" data-fr-coupon-close>Continue</button>';
      }
      var unlockRow = '<div class="fr-cp__unlock"><span class="fr-cp__unlock-icon">' + ICON.mail20 + '</span><span class="fr-cp__unlock-label">Get it unlocked by</span><span class="fr-cp__unlock-date">' + F.escapeHtml(ci.unlockIso ? F.formatDate(ci.unlockIso) : "your delivery + 7 days") + "</span></div>";
      var how = '<div class="fr-cp__card"><div class="fr-cp__howhead"><div class="fr-cp__howhead-left"><span class="fr-cp__howicon">' + ICON.howto + '</span><span class="fr-cp__howtitle">How to Unlock Gift coupon</span></div></div>' +
        '<div class="fr-cp__howbody">' + trackerHtml(order, ci) + unlockRow + '</div><div class="fr-cp__terms">' + TERMS + "</div></div>";
      return head + '<div class="fr-cp__col">' + drawerCouponCard(li, order, ci) + how + "</div>" +
        '<button type="button" class="fr-cp__cta" data-fr-coupon-close>Continue</button>';
    }
    function openDrawer(order) {
      var li = (order.lineItems || []).filter(isCoupon)[0];
      if (!li || !drawer) return;
      drawerBody.innerHTML = drawerHtml(order, li);
      drawer.hidden = false;
      lockScroll();
      // Force a reflow so the closed transform is committed, then flip .is-open
      // so the slide/fade actually transitions. Same technique as
      // frido-mini-pdp.js — rAF is paused in background tabs, which would leave
      // the sheet stuck off-screen.
      void drawer.offsetHeight;
      drawer.classList.add("is-open");
      drawer.querySelectorAll("[data-fr-coupon-close]").forEach(function (el) { el.addEventListener("click", closeDrawer); });
    }
    function closeDrawer() {
      if (!drawer || drawer.hidden) return;
      // Play the exit transition before hiding, else the sheet vanishes instantly.
      drawer.classList.remove("is-open");
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        drawer.hidden = true;
        drawerBody.innerHTML = "";
        unlockScroll();
      };
      var sheet = drawer.querySelector(".fr-cp__sheet");
      var dur = sheet ? parseFloat(getComputedStyle(sheet).transitionDuration) : 0;
      if (!dur) return finish();
      sheet.addEventListener("transitionend", function h(e) {
        if (e.target !== sheet || e.propertyName !== "transform") return;
        sheet.removeEventListener("transitionend", h);
        finish();
      });
      setTimeout(finish, 420);
    }
    if (drawer) {
      drawer.querySelector(".fr-cp__overlay").addEventListener("click", closeDrawer);
      if (escDrawerFn) document.removeEventListener("keydown", escDrawerFn);
      escDrawerFn = function (e) { if (e.key === "Escape" && !drawer.hidden) closeDrawer(); };
      document.addEventListener("keydown", escDrawerFn);
    }
    if (trackDrawer) {
      trackDrawer.querySelectorAll("[data-fr-track-close]").forEach(function (el) { el.addEventListener("click", closeTrackDrawer); });
      if (escTrackFn) document.removeEventListener("keydown", escTrackFn);
      escTrackFn = function (e) { if (e.key === "Escape" && trackDrawer.classList.contains("is-open")) closeTrackDrawer(); };
      document.addEventListener("keydown", escTrackFn);
      // Delegate: a phase row's chevron toggles its scan sub-list.
      trackDrawer.addEventListener("click", function (e) {
        var head = e.target.closest("[data-fr-tl-toggle]");
        if (!head) return;
        var row = head.closest(".fr-tl__row");
        if (row) row.classList.toggle("is-open");
      });
    }

    // Animated accordions (Order Details / Price Breakdown / policy / eligibility)
    // live in both the main detail area and the track drawer, so delegate at the
    // document: toggling .is-open animates the grid-rows clip via CSS.
    if (!accDelegated) {
      accDelegated = true;
      document.addEventListener("click", function (e) {
        var sum = e.target.closest && e.target.closest("[data-fr-acc]");
        if (!sum) return;
        var acc = sum.closest(".fr-od__acc");
        if (!acc) return;
        var isOpen = acc.classList.toggle("is-open");
        sum.setAttribute("aria-expanded", isOpen ? "true" : "false");
      });
    }

    // ── Cancel + Track modals ───────────────────────────────────────────
    function openModal(html) { modalBody.innerHTML = html; modal.hidden = false; lockScroll(); }
    function closeModal() { modal.hidden = true; modalBody.innerHTML = ""; unlockScroll(); }
    root.querySelectorAll("[data-fr-modal-close]").forEach(function (el) { el.addEventListener("click", closeModal); });
    if (escModalFn) document.removeEventListener("keydown", escModalFn);
    escModalFn = function (e) { if (e.key === "Escape" && !modal.hidden) closeModal(); };
    document.addEventListener("keydown", escModalFn);
    function rebindClose() {
      root.querySelectorAll("[data-fr-modal-close]").forEach(function (el) { el.removeEventListener("click", closeModal); el.addEventListener("click", closeModal); });
    }

    // ── Cancel: right-slide drawer (Figma: "Request Cancellation") ───────
    // Mirrors the order-details drawer: slides in from the right, 450px on
    // desktop / full-width on mobile. Order card + "Reason for Cancellation"
    // accordion + single-select reason list (checkbox look) + red Submit.
    var CC_CHEVRON = "<svg viewBox='0 0 24 24' width='20' height='20' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'></path></svg>";
    var CC_CHECK = "<svg viewBox='0 0 24 24' width='13' height='13' fill='none' stroke='currentColor' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'><path d='M20 6L9 17l-5-5'></path></svg>";
    var CANCEL_CSS = ".fr-cdrawer{position:fixed;inset:0;z-index:300}.fr-cdrawer[hidden]{display:none}.fr-cdrawer__overlay{position:absolute;inset:0;background:rgba(19,19,19,.35);opacity:0;transition:opacity .25s ease}.fr-cdrawer.is-open .fr-cdrawer__overlay{opacity:1}.fr-cdrawer__panel{position:absolute;top:0;right:0;height:100%;width:450px;max-width:92vw;background:#fff;display:flex;flex-direction:column;transform:translateX(100%);transition:transform .28s cubic-bezier(.4,0,.2,1);box-shadow:-8px 0 24px rgba(0,0,0,.1)}.fr-cdrawer.is-open .fr-cdrawer__panel{transform:translateX(0)}@media (max-width:640px){.fr-cdrawer__panel{width:100%;max-width:100%}}@media (prefers-reduced-motion:reduce){.fr-cdrawer__panel,.fr-cdrawer__overlay{transition:none}}.fr-cdrawer__head{flex:0 0 auto;display:flex;align-items:center;gap:12px;min-height:60px;padding:14px 16px;border-bottom:1px solid #eee}.fr-cdrawer__back{display:inline-flex;align-items:center;justify-content:center;width:24px;height:24px;padding:0;border:0;background:transparent;color:#131313;cursor:pointer;flex:0 0 auto}.fr-cdrawer__title{flex:1 1 auto;font-size:19px;font-weight:700;color:#131313}.fr-cdrawer__body{flex:1 1 auto;overflow-y:auto;padding:16px}.fr-cdrawer__foot{flex:0 0 auto;padding:14px 16px 18px;border-top:1px solid #f0f0f0;background:#fff}.fr-cdrawer__submit{width:100%;height:52px;border:0;border-radius:14px;background:#DC3634;color:#fff;font-family:inherit;font-size:16px;font-weight:700;cursor:pointer}.fr-cdrawer__submit[disabled]{opacity:.55;cursor:default}.fr-cdrawer__msg{margin:10px 2px 0;font-size:13px}.fr-cdrawer__msg--err{color:#c0392b}.fr-cdrawer__msg--ok{color:#1f7a3d}.fr-cc__ocard{border:1px solid #ededed;border-radius:16px;padding:16px}.fr-cc__num{font-size:15px;font-weight:600;color:#131313;margin-bottom:12px}.fr-cc__prow{display:flex;gap:14px}.fr-cc__thumb{width:70px;height:70px;border-radius:10px;object-fit:cover;flex:0 0 70px;background:#f3f3f3}.fr-cc__thumb--ph{background:linear-gradient(135deg,#f3f3f3,#e7e7e7)}.fr-cc__info{min-width:0}.fr-cc__t{font-size:14px;font-weight:600;color:#131313;line-height:1.35}.fr-cc__q{font-size:13px;color:#8a8a8a;margin-top:6px}.fr-cc__p{font-size:13px;color:#8a8a8a;margin-top:2px}.fr-cc__acc{border:1px solid #ededed;border-radius:16px;margin-top:14px;overflow:hidden}.fr-cc__acc-hd{display:flex;align-items:center;justify-content:space-between;width:100%;padding:18px;border:0;background:transparent;font-family:inherit;font-size:16px;font-weight:700;color:#131313;cursor:pointer;text-align:left}.fr-cc__acc-hd svg{transition:transform .25s;flex:0 0 auto}.fr-cc__acc.is-open .fr-cc__acc-hd svg{transform:rotate(180deg)}.fr-cc__acc-clip{display:grid;grid-template-rows:1fr;transition:grid-template-rows .3s cubic-bezier(.4,0,.2,1)}.fr-cc__acc:not(.is-open) .fr-cc__acc-clip{grid-template-rows:0fr}.fr-cc__acc-clip>.fr-cc__acc-body{overflow:hidden;min-height:0}.fr-cc__acc-body{display:flex!important;flex-direction:column!important;padding:0 18px 8px}.fr-cc__opt{display:flex;width:100%;align-items:center;gap:14px;padding:11px 0;cursor:pointer}.fr-cc__opt input{position:absolute;opacity:0;width:0;height:0}.fr-cc__box{width:22px;height:22px;border-radius:6px;border:1.6px solid #d3d3d3;flex:0 0 22px;display:flex;align-items:center;justify-content:center;background:#fff;transition:.15s}.fr-cc__box svg{opacity:0}.fr-cc__opt.is-on .fr-cc__box{background:#307FE2;border-color:#307FE2}.fr-cc__opt.is-on .fr-cc__box svg{opacity:1}.fr-cc__lbl{font-size:14px;color:#4d4d4d}.fr-cc__opt.is-on .fr-cc__lbl{color:#131313}.fr-cc__err{font-size:13px;color:#c0392b;padding:8px 0}";

    var cancelDrawer = null;
    function injectCancelStyles() {
      if (document.getElementById("fr-cc-styles")) return;
      var s = document.createElement("style");
      s.id = "fr-cc-styles"; s.textContent = CANCEL_CSS;
      document.head.appendChild(s);
    }
    function ensureCancelDrawer() {
      if (cancelDrawer && document.body.contains(cancelDrawer)) return cancelDrawer;
      injectCancelStyles();
      var d = document.createElement("div");
      d.className = "fr-cdrawer"; d.hidden = true;
      d.innerHTML =
        '<div class="fr-cdrawer__overlay" data-fr-cancel-close></div>' +
        '<div class="fr-cdrawer__panel" role="dialog" aria-modal="true" aria-label="Request Cancellation">' +
          '<div class="fr-cdrawer__head">' +
            '<button type="button" class="fr-cdrawer__back" data-fr-cancel-close aria-label="Back">' + ICON.back + '</button>' +
            '<span class="fr-cdrawer__title">Request Cancellation</span>' +
          '</div>' +
          '<div class="fr-cdrawer__body" data-fr-cancel-body></div>' +
          '<div class="fr-cdrawer__foot">' +
            '<button type="button" class="fr-cdrawer__submit" data-fr-cancel-submit>Submit Request</button>' +
            '<p class="fr-cdrawer__msg" data-fr-cancel-msg></p>' +
          '</div>' +
        '</div>';
      document.body.appendChild(d);
      d.querySelectorAll("[data-fr-cancel-close]").forEach(function (el) { el.addEventListener("click", closeCancel); });
      if (escCancelFn) document.removeEventListener("keydown", escCancelFn);
      escCancelFn = function (e) { if (e.key === "Escape" && cancelDrawer && cancelDrawer.classList.contains("is-open")) closeCancel(); };
      document.addEventListener("keydown", escCancelFn);
      cancelDrawer = d;
      return d;
    }
    function closeCancel() {
      if (!cancelDrawer) return;
      cancelDrawer.classList.remove("is-open");
      unlockScroll();
      setTimeout(function () { if (cancelDrawer) cancelDrawer.hidden = true; }, 300);
    }
    function orderCardHtml(order) {
      var li = ((order && order.lineItems) || []).filter(function (l) { return !isCoupon(l); })[0] || {};
      var img = liImg(li);
      var thumb = img
        ? '<img class="fr-cc__thumb" src="' + F.escapeHtml(img) + '" alt="' + F.escapeHtml(liAlt(li)) + '">'
        : '<span class="fr-cc__thumb fr-cc__thumb--ph"></span>';
      var price = F.money(liPaid(li)) || "";
      return '<div class="fr-cc__ocard">' +
          '<div class="fr-cc__num">' + F.escapeHtml((order && order.name) || "") + '</div>' +
          '<div class="fr-cc__prow">' + thumb +
            '<div class="fr-cc__info">' +
              '<div class="fr-cc__t">' + F.escapeHtml(li.title || "") + '</div>' +
              '<div class="fr-cc__q">QTY: ' + (li.quantity || 1) + '</div>' +
              (price ? '<div class="fr-cc__p">' + F.escapeHtml(price) + '</div>' : '') +
            '</div>' +
          '</div>' +
        '</div>';
    }
    // Self-serve cancellation is only offered for the first 30 minutes after the order
    // is placed. After that the drawer is not opened at all — the shopper is handed to
    // support on WhatsApp with the order number pre-filled.
    var CANCEL_WINDOW_MS = 30 * 60 * 1000;
    // 91 = India country code. api.whatsapp.com requires a full international number; a bare
    // 10-digit number makes WhatsApp guess the country from the viewer's locale, which is
    // unreliable and breaks outright for anyone outside India. Matches the convention used by
    // the other WhatsApp links in this theme (917028492155). Spec gave 7498476544.
    var CANCEL_WA_PHONE = "917498476544";

    /* Exchanges never get the self-serve window.

       An exchange raised through ClickPost lands as its own order whose name carries an
       `_EX` suffix — e.g. #MF0223406940_EX445. Cancelling one from the storefront would
       leave the exchange out of step with the original order it was raised against, so
       Frido wants every exchange handed to support the moment it is placed, however new
       it is.

       Matched on a bare `_EX` rather than an anchored `_EX<digits>$`, because the two ways
       this can be wrong are not equally costly. Reading a NORMAL order as an exchange only
       costs that shopper the self-serve drawer — support can still cancel it for them.
       Reading an EXCHANGE as a normal order permits exactly the cancellation this exists
       to prevent. The looser test fails in the cheaper direction. */
    var EXCHANGE_RE = /_EX/i;

    function isExchangeOrder(order) {
      return EXCHANGE_RE.test(String((order && order.name) || ""));
    }

    function withinCancelWindow(order) {
      if (!order || !order.createdAt) return false;
      if (isExchangeOrder(order)) return false;
      var placedAt = new Date(order.createdAt).getTime();
      // Unparseable timestamp: fail closed and route to support rather than offering a
      // cancellation we cannot prove is inside the 30-minute policy window.
      if (!placedAt || isNaN(placedAt)) return false;
      return Date.now() - placedAt < CANCEL_WINDOW_MS;
    }

    function whatsappCancelUrl(order) {
      var oName = String((order && order.name) || "").replace(/#/g, "");
      /* Name the exchange as an exchange, so support does not have to work out which of
         the two orders the shopper means before they can act on it. */
      var text = isExchangeOrder(order)
        ? "Hi, my exchange order number is: " + oName + ". I want to cancel this exchange."
        : "Hi, my order number is: " + oName + ". I want to cancel this order.";
      // encodeURIComponent is required — the message has spaces, a colon and full stops.
      return "https://api.whatsapp.com/send?phone=" + CANCEL_WA_PHONE +
             "&text=" + encodeURIComponent(text);
    }

    function openCancel() {
      if (!withinCancelWindow(current)) {
        var waUrl = whatsappCancelUrl(current);
        // Runs inside a click handler, so this is user-initiated and not popup-blocked;
        // fall back to a same-tab navigation if a browser blocks it anyway.
        var win = window.open(waUrl, "_blank", "noopener");
        if (!win) window.location.href = waUrl;
        return;
      }
      var d = ensureCancelDrawer();
      var body = d.querySelector("[data-fr-cancel-body]");
      var msg = d.querySelector("[data-fr-cancel-msg]");
      var submit = d.querySelector("[data-fr-cancel-submit]");
      msg.textContent = ""; msg.className = "fr-cdrawer__msg";
      submit.disabled = false; submit.textContent = "Submit Request";
      body.innerHTML = orderCardHtml(current) +
        '<div class="fr-cc__acc is-open" data-fr-cc-acc>' +
          '<button type="button" class="fr-cc__acc-hd" data-fr-cc-acc-tgl><span>Reason for Cancellation</span>' + CC_CHEVRON + '</button>' +
          '<div class="fr-cc__acc-clip"><div class="fr-cc__acc-body" data-fr-cc-list><div class="fr-skel-line"></div><div class="fr-skel-line" style="width:70%"></div></div></div>' +
        '</div>';
      d.hidden = false; void d.offsetWidth; d.classList.add("is-open");
      lockScroll();
      var acc = body.querySelector("[data-fr-cc-acc]");
      body.querySelector("[data-fr-cc-acc-tgl]").addEventListener("click", function () { acc.classList.toggle("is-open"); });
      submit.onclick = submitCancel;
      F.proxy("/order/cancelReasons", { query: { orderId: orderId } })
        .then(function (reasons) {
          if (!Array.isArray(reasons) || !reasons.length) reasons = [{ id: 0, reason: "Other" }];
          var opts = reasons.map(function (r) {
            return '<label class="fr-cc__opt"><input type="radio" name="fr-cc-reason" value="' + F.escapeHtml(String(r.id)) + '"><span class="fr-cc__box">' + CC_CHECK + '</span><span class="fr-cc__lbl">' + F.escapeHtml(r.reason) + '</span></label>';
          }).join("");
          var list = body.querySelector("[data-fr-cc-list]");
          if (list) list.innerHTML = opts;
          body.querySelectorAll(".fr-cc__opt input").forEach(function (inp) {
            inp.addEventListener("change", function () {
              body.querySelectorAll(".fr-cc__opt").forEach(function (o) { o.classList.remove("is-on"); });
              inp.closest(".fr-cc__opt").classList.add("is-on");
            });
          });
        })
        .catch(function (err) {
          var list = body.querySelector("[data-fr-cc-list]");
          var unavailable = err && err.code === "PROVIDER_NOT_CONFIGURED";
          if (list) list.innerHTML = '<p class="fr-cc__err">' + (unavailable ? "Order cancellation isn't available right now. Please contact support." : F.escapeHtml(err.message || "Could not load cancellation reasons.")) + "</p>";
        });
    }
    // Tell the order-list (if it's on the page, e.g. the desktop shell) to
    // reflect the cancellation in place — no page reload needed.
    function notifyCancelled() {
      try { window.dispatchEvent(new CustomEvent("frido:order-cancelled", { detail: { orderId: orderId } })); } catch (e) {}
    }
    function submitCancel() {
      var d = cancelDrawer; if (!d) return;
      var checked = d.querySelector('input[name="fr-cc-reason"]:checked');
      var reasonId = checked ? checked.value : "";
      var btn = d.querySelector("[data-fr-cancel-submit]");
      var msg = d.querySelector("[data-fr-cancel-msg]");
      if (!reasonId) { msg.className = "fr-cdrawer__msg fr-cdrawer__msg--err"; msg.textContent = "Please select a reason for cancellation."; return; }
      btn.disabled = true; btn.textContent = "Submitting…"; msg.textContent = ""; msg.className = "fr-cdrawer__msg";
      F.proxy("/order/cancel", { method: "POST", body: { orderId: orderId, reasonId: reasonId, customReason: "" } })
        .then(function () {
          notifyCancelled();
          msg.className = "fr-cdrawer__msg fr-cdrawer__msg--ok";
          msg.textContent = "Cancellation requested. It can take a little while to reflect — refreshing…";
          setTimeout(function () { closeCancel(); load(); }, 1500);
        })
        .catch(function (err) {
          if (err && err.code === "BAD_RESPONSE") {
            notifyCancelled();
            msg.className = "fr-cdrawer__msg fr-cdrawer__msg--ok";
            msg.textContent = "Cancellation request submitted. It can take a little while to process — refreshing…";
            setTimeout(function () { closeCancel(); load(); }, 1800);
            return;
          }
          btn.disabled = false; btn.textContent = "Submit Request";
          msg.className = "fr-cdrawer__msg fr-cdrawer__msg--err";
          msg.textContent = err.message || "Could not cancel. Please contact support.";
        });
    }
    function cancelError(err) {
      var unavailable = err && err.code === "PROVIDER_NOT_CONFIGURED";
      openModal("<h2>Cancel order</h2><p>" + (unavailable ? "Order cancellation isn't available right now. Please contact support." : F.escapeHtml(err.message || "Could not load cancellation reasons.")) + "</p>");
      rebindClose();
    }

    // ── Tracking timeline (sliding drawer; replaces the old Track modal) ─
    // Opens a right-side drawer and lazily fetches the live courier phase
    // timeline from /order/tracking. Response shape:
    // { available, courier, waybill, tracking: { timeline: { phases[] } } };
    // each phase carries phase_name, phase_key, last_update, and a statuses[]
    // scan list. The result is cached (data-loaded) so re-opening is instant.
    function openTrackDrawer() {
      if (!trackDrawer || !trackDrawerBody) return;
      var helpEl = trackDrawer.querySelector("[data-fr-track-help]");
      if (helpEl) helpEl.href = buildHelpUrl(current && current.name) || "#";
      trackDrawer.classList.add("is-open");
      lockScroll();
      if (trackDrawerBody.getAttribute("data-loaded")) return;
      trackDrawerBody.innerHTML =
        '<div class="fr-tl__card"><div class="fr-tl__msg"><span class="fr-skel-line"></span><span class="fr-skel-line" style="width:60%"></span></div></div>';
      F.proxy("/order/tracking", { query: { orderId: orderId } })
        .then(function (res) {
          trackDrawerBody.setAttribute("data-loaded", "1");
          trackDrawerBody.innerHTML = trackDrawerHtml(res);
        })
        .catch(function () {
          trackDrawerBody.innerHTML =
            '<div class="fr-tl__card">' + trackMessage("Tracking isn't available right now. Please try again later.") + "</div>";
        });
    }
    function closeTrackDrawer() {
      if (!trackDrawer) return;
      trackDrawer.classList.remove("is-open");
      unlockScroll();
    }

    // Full drawer body: a status hero (current status + ETA/delivered date +
    // courier/AWB + courier-site link), the phase timeline, the shipment's
    // products, and a help CTA. Status/phases come from the tracking response;
    // the courier link + products come from the already-loaded order (`current`).
    function trackDrawerHtml(res) {
      var elig = policyBody ? accordion(policyTitle, ICON.receipt, policyBodyHtml(), false) : "";
      return (
        trackProductCard(res) +
        '<div class="fr-tl__card">' + trackTimelineHtml(res) + "</div>" +
        elig
      );
    }

    function prettyStatus(t) {
      if (!t) return "In transit";
      return String(t).toLowerCase().replace(/_/g, " ").replace(/^\w/, function (c) { return c.toUpperCase(); });
    }

    // Per-item badge reflecting the shipment's current status.
    function trackBadge(res) {
      var cur = (res && res.tracking && res.tracking.current_status) || {};
      var code = String(cur.text || "").toUpperCase();
      var eta = res && res.tracking && res.tracking.delivery_info && res.tracking.delivery_info.expected_date;
      if (code === "OUT_FOR_DELIVERY") return { text: "Out for delivery", tone: "blue" };
      if (code === "DELIVERED") return { text: cur.timestamp ? "Delivered " + F.formatDate(String(cur.timestamp).replace(" ", "T")) : "Delivered", tone: "gray" };
      if (code.indexOf("CANCEL") !== -1) return { text: "Cancelled", tone: "red" };
      if (eta) return { text: "Expected by " + F.formatDate(String(eta).replace(" ", "T")), tone: "gray" };
      return { text: cur.text ? prettyStatus(cur.text) : "Tracking", tone: "gray" };
    }

    function trackItemMeta(li) {
      var parts = [];
      var opts = (Array.isArray(li.selectedOptions) ? li.selectedOptions : []).filter(function (o) {
        return o && o.name && o.value && String(o.value).toLowerCase() !== "default title";
      });
      if (opts.length) {
        opts.forEach(function (o) { parts.push(o.name + ": " + o.value); });
      } else {
        var color = li.variantTitle || null;
        if (color && color.toLowerCase() !== "default title") parts.push(color);
      }
      parts.push("Qty: " + (li.quantity || 1));
      var m = li.discountedTotalSet || li.lineTotal;
      var paid = m && m.amount != null ? moneyOfAmount(Number(m.amount), m.currencyCode) : "";
      if (paid) parts.push("Paid: " + paid);
      return '<span class="fr-td__pmeta">' + parts.map(function (p, i) {
        return (i ? '<span class="fr-td__pdot"></span>' : "") + "<span>" + F.escapeHtml(p) + "</span>";
      }).join("") + "</span>";
    }

    function trackProductCard(res) {
      var items = ((current && current.lineItems) || []).filter(function (li) { return !isCoupon(li); });
      if (!items.length) return "";
      var badge = trackBadge(res);
      var badgeHtml = '<span class="fr-td__badge fr-td__badge--' + badge.tone + '">' + ICON.truck14 + "<span>" + F.escapeHtml(badge.text) + "</span></span>";
      var rows = items.map(function (li, i) {
        var u = liImg(li);
        var img = u ? '<img src="' + F.escapeHtml(u) + '" alt="' + F.escapeHtml(liAlt(li)) + '" loading="lazy">' : "";
        return (i ? '<div class="fr-td__pdiv"></div>' : "") +
          '<div class="fr-td__prow"><span class="fr-td__pthumb">' + img + "</span>" +
          '<div class="fr-td__pmain">' + badgeHtml +
          '<span class="fr-td__ptitle">' + F.escapeHtml(li.title || "") + "</span>" +
          trackItemMeta(li) + "</div></div>";
      }).join("");
      return '<div class="fr-tl__card fr-td__pcard">' + rows + "</div>";
    }

    function trackMessage(text) {
      return '<div class="fr-tl__msg">' + F.escapeHtml(text) + "</div>";
    }

    // Node style per phase_key; the latest in-flight phase renders as the blue
    // "current" node, earlier reached phases as black checks, terminal phases in
    // their own colour (green/red/blue). Whatever phases the API returns
    // (including cancelled/returned/refund) render generically — no hardcoding.
    var TL_NODE = {
      delivered: "delivered", cancelled: "cancelled", cancelled_order: "cancelled",
      returned: "returned", refund_completed: "refund",
    };

    // Just-placed state (order confirmed, not yet shipped): a 2-node timeline —
    // "Order Confirmed" (blue package node + order date) then the greyed
    // "Delivery expected by <edd>" pending node. Reuses the shipped-timeline node
    // and rail styling so it is visually identical (see Figma "just placed"). The
    // date comes from the order's evolv_edd_date (backend estimatedDeliveryDate).
    function justPlacedTimelineHtml(edd) {
      var placed = current && current.createdAt ? F.formatDate(current.createdAt) : "";
      var dateHtml = placed ? '<span class="fr-tl__date">' + F.escapeHtml(placed) + "</span>" : "";
      var confirmed =
        '<div class="fr-tl__row">' +
        '<div class="fr-tl__rail"><span class="fr-tl__node fr-tl__node--current">' + ICON.box + "</span>" +
        '<span class="fr-tl__conn"></span></div>' +
        '<div class="fr-tl__body"><div class="fr-tl__head fr-tl__head--static">' +
        '<span class="fr-tl__titledate"><span class="fr-tl__title">Order Confirmed</span>' +
        dateHtml + "</span></div></div>" +
        "</div>";
      var pending = trackPendingRow("Delivery expected by " + F.formatDate(String(edd).replace(" ", "T")));
      return '<div class="fr-tl">' + confirmed + pending + "</div>";
    }

    function trackTimelineHtml(res) {
      if (!res || !res.available) {
        // Not shipped yet: if the order carries an expected delivery date
        // (evolv_edd_date → backend estimatedDeliveryDate), render the just-placed
        // timeline instead of a bare "not available" message. Cancelled orders
        // fall through to the message (no delivery to expect).
        var edd = current && current.estimatedDeliveryDate;
        if (edd && !(current && F.isCancelled(current))) return justPlacedTimelineHtml(edd);
        return trackMessage(
          res && res.reason === "NO_SCANS"
            ? "Your order is on its way — tracking updates appear here once the courier scans it."
            : "Tracking isn't available yet. Please check back once your order ships.",
        );
      }
      var timeline = res.tracking && res.tracking.timeline;
      var phases = (timeline && timeline.phases) || [];
      if (!phases.length) return trackMessage("No tracking updates yet.");
      var lastKey = String(phases[phases.length - 1].phase_key || "").toLowerCase();
      var terminal = /deliver|cancel|return|refund/.test(lastKey);
      var eta = res.tracking && res.tracking.delivery_info && res.tracking.delivery_info.expected_date;
      var pending = !terminal && eta
        ? trackPendingRow("Delivery expected by " + F.formatDate(String(eta).replace(" ", "T")))
        : "";
      var rows = phases
        .map(function (p, i) { return trackPhaseRow(p, !pending && i === phases.length - 1); })
        .join("");
      return '<div class="fr-tl">' + rows + pending + "</div>";
    }

    function trackPhaseRow(phase, isLast) {
      var special = TL_NODE[String(phase.phase_key || "").toLowerCase()];
      var node;
      if (special === "cancelled") node = '<span class="fr-tl__node fr-tl__node--cancelled">' + ICON.xmark + "</span>";
      else if (special === "returned") node = '<span class="fr-tl__node fr-tl__node--returned">' + ICON.undo + "</span>";
      else if (special === "refund") node = '<span class="fr-tl__node fr-tl__node--refund">' + ICON.rupee + "</span>";
      else if (special === "delivered") node = '<span class="fr-tl__node fr-tl__node--delivered">' + ICON.box + "</span>";
      else if (phase.is_current) node = '<span class="fr-tl__node fr-tl__node--current">' + ICON.box + "</span>";
      else node = '<span class="fr-tl__node fr-tl__node--done">' + ICON.stepCheck + "</span>";
      var conn = isLast ? "" : '<span class="fr-tl__conn"></span>';
      // Frido timestamps are "YYYY-MM-DD HH:mm:ss" — normalise the space to T so
      // Date parsing is reliable across browsers.
      var when = phase.last_update || phase.first_update;
      var dateStr = when ? F.formatDate(String(when).replace(" ", "T")) : "";
      var dateHtml = dateStr ? '<span class="fr-tl__date">' + F.escapeHtml(dateStr) + "</span>" : "";
      var head =
        '<span class="fr-tl__titledate"><span class="fr-tl__title">' +
        F.escapeHtml(phase.phase_name || "") + "</span>" + dateHtml + "</span>";
      var scans = Array.isArray(phase.statuses) ? phase.statuses : [];
      var body = scans.length
        ? '<button type="button" class="fr-tl__head" data-fr-tl-toggle>' + head +
          '<span class="fr-tl__chev">' + ICON.tlChev + "</span></button>" +
          '<div class="fr-tl__scans"><div class="fr-tl__scans-inner">' + scans.map(trackScanRow).join("") + "</div></div>"
        : '<div class="fr-tl__head fr-tl__head--static">' + head + "</div>";
      return (
        '<div class="fr-tl__row">' +
        '<div class="fr-tl__rail">' + node + conn + "</div>" +
        '<div class="fr-tl__body">' + body + "</div>" +
        "</div>"
      );
    }

    function trackPendingRow(label) {
      return (
        '<div class="fr-tl__row">' +
        '<div class="fr-tl__rail"><span class="fr-tl__node fr-tl__node--pending"></span></div>' +
        '<div class="fr-tl__body"><div class="fr-tl__head fr-tl__head--static">' +
        '<span class="fr-tl__titledate"><span class="fr-tl__title fr-tl__title--pending">' +
        F.escapeHtml(label) + "</span></span></div></div>" +
        "</div>"
      );
    }

    function trackScanRow(s) {
      var text = s.remarks || s.status_text || "";
      var loc = s.location ? ' <span class="fr-tl__scan-loc">' + F.escapeHtml(s.location) + "</span>" : "";
      var when = s.formatted_date || s.timestamp || "";
      return (
        '<div class="fr-tl__scan"><span class="fr-tl__scan-text">' + F.escapeHtml(text) + loc + "</span>" +
        (when ? '<span class="fr-tl__scan-time">' + F.escapeHtml(String(when)) + "</span>" : "") +
        "</div>"
      );
    }

    // ── Load ────────────────────────────────────────────────────────────
    function showState(html) { detailEl.innerHTML = '<div class="fr-od__state">' + html + "</div>"; }
    function showSkeleton() {
      detailEl.innerHTML =
        '<div class="fr-od__card"><div class="fr-od__dbody">' +
        '<div class="fr-skel-line" style="width:60%"></div><div class="fr-skel-line"></div><div class="fr-skel-line" style="width:80%"></div>' +
        "</div></div>";
    }
    function load() {
      if (!orderId) { showState('<h2>Order not found</h2><p>No order was specified.</p><a href="' + listPath + '">Back to orders</a>'); return; }
      showSkeleton();
      F.proxy("/order", { query: { orderId: orderId } })
        .then(render)
        .catch(function (err) {
          if (err && err.code === "NOT_LOGGED_IN") {
            var ret = encodeURIComponent("/pages/account");
            showState('<h2>Please log in</h2><p>Log in to view this order.</p><a href="' + loginPath + "?return_to=" + ret + '">Log in</a>');
            return;
          }
          showState('<h2>Couldn\'t load this order</h2><p>' + F.escapeHtml(err.message || "Please try again.") + '</p><button type="button" class="fr-od__retry" data-fr-retry>Retry</button>');
          var retry = detailEl.querySelector("[data-fr-retry]");
          if (retry) retry.addEventListener("click", load);
        });
    }

    load();
  }

  // Exposed so the desktop account shell can mount order details into a drawer
  // container on demand (init reads the order id from data-order-id).
  window.FridoOrderDetails = { init: init };

  // Auto-boot the standalone page; skip drawer containers (the shell inits those).
  function boot() { document.querySelectorAll("[data-order-details]:not([data-fr-drawer])").forEach(init); }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
